import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  FaArrowRight,
  FaExternalLinkAlt,
  FaGlobe,
  FaRobot,
  FaUsers,
  FaHeadset,
} from 'react-icons/fa';

const Products = () => {
  const [sectionRef, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const tabs = [
    { id: 'all', label: 'All' },
    { id: 'Live Demo', label: 'Live Demo' },
    { id: 'Live Demo Beta', label: 'Live Demo Beta' },
    { id: 'Video Demo Post', label: 'Video Demo Post' },
  ];


  const [activeTab, setActiveTab] = React.useState('all');

  const demos = [
    // Existing cards (kept as-is)
    {


      id: 1,
      title: 'Instant Lead Response Automation',
      description:
        'Automatically sends acknowledgment emails the moment a lead is created, ensuring immediate engagement and eliminating response delays in early-stage customer interaction.',
      outcome: 'Zero-delay lead engagement',
      tag: 'Video Demo Post',
      link: 'https://www.linkedin.com/posts/sronzer_salesforce-salesforceflow-crm-activity-7466205478448988160-ztb2/',
      icon: FaUsers,
      image:
        'https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=1200&q=80',
    },
    {
      id: 2,
      title: 'Lead Action Automation',
      description:
        'Instantly generates and assigns tasks when a lead is created, ensuring timely follow-ups, clear ownership, and consistent execution across the sales pipeline.',
      outcome: 'Eliminates missed follow-ups',
      tag: 'Video Demo Post',
      link: 'https://www.linkedin.com/posts/sronzer_salesforce-salesforceflow-crm-activity-7464778581835939840-VPR9/',
      icon: FaUsers,
      image:
        'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=80',
    },
    {
      id: 3,
      title: 'Case Action Automation',
      description:
        'Automatically creates and assigns tasks on case creation, enforcing structured service workflows, improving accountability, and ensuring SLA adherence.',
      outcome: 'Predictable service execution',
      tag: 'Video Demo Post',
      link: 'https://www.linkedin.com/posts/sronzer_salesforce-salesforceflow-servicecloud-activity-7465503355000307712-Hbb8/',
      icon: FaHeadset,
      image:
        'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=1200&q=80',
    },
    {
      id: 4,
      title: 'CRM Execution Architecture',
      description:
        'Designs Salesforce Flows as state-driven execution systems that enforce process continuity, ownership, and deterministic outcomes across the CRM lifecycle.',
      outcome: 'System-driven process governance',
      tag: 'Video Demo Post',
      link: 'https://www.linkedin.com/posts/sronzer_salesforce-salesforceflow-crmarchitecture-activity-7464053798810927104-s-eI/',
      icon: FaGlobe,
      image:
        '/images/crmarchitecture.png',
    },

    // NEW: Live demo cards (use-case driven, unsplash-style images)
    {
      id: 5,
      title: 'Sronzer Inquiry Form',
      description:
        'A CRM lead capture flow that creates Salesforce Lead records and triggers automated follow-ups to accelerate sales pipeline initiation.',

      outcome: 'Real-time lead capture & CRM entry',
      tag: 'Live Demo',
      link: 'https://orgfarm-d22a0a94da-dev-ed.develop.my.site.com/SronzerInquiryForm',
      icon: FaUsers,
      image:
        'https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=1200&q=80&v=2&exp=1',
      isLive: true,
    },
    {
      id: 6,
      title: 'Hospital Chatbot Case System',
      description:
        'A healthcare chatbot that creates Salesforce Case records from patient interactions, enabling structured service workflows and faster next-step handling.',

      outcome: 'Automated case creation via chatbot',
      tag: 'Live Demo',
      link: 'https://orgfarm-d22a0a94da-dev-ed.develop.my.site.com/hospitallandingpage',
      icon: FaHeadset,
      image:
        '/images/chatbotscreen.png',
      isLive: true,
    },
    {
      id: 7,
      title: 'Acne Detection Survey (POC)',
      description:
        'An image-based healthcare survey that captures patient photos and triggers CRM workflows, turning structured inputs into Salesforce-ready actions.',

      outcome: 'Image-based data capture for workflows',
      tag: 'Live Demo',
      link: 'https://orgfarm-d22a0a94da-dev-ed.develop.my.site.com/acneSurvey',
      icon: FaRobot,
      image:
        'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=80&v=3',
      isLive: true,
    },

    // NEW: Live demo beta cards
    {
      id: 8,
      title: 'Acne AI Skin Test',
      description:
        'Beta version: an AI-powered acne skin test experience that captures inputs and triggers CRM-ready workflows (beta software).',
      outcome: 'Beta skin test → workflow trigger',
      tag: 'Live Demo Beta',
      link: 'https://acneaianalysis-production.up.railway.app/',
      icon: FaRobot,
      image:
        '/images/acneaiskintest.png',
      isLive: true,
    },
    {
      id: 9,
      title: 'Shop D2C CRM Platform',
      description:
        'Beta version: a D2C CRM platform for ecommerce shops to manage customers, orders, and automations using connected workflows (beta software).',
      outcome: 'Beta D2C CRM → unified customer ops',
      tag: 'Live Demo Beta',
      link: 'https://glorious-nurturing-production-5c87.up.railway.app/',
      icon: FaUsers,
      image:
        '/images/shopd2c.png',
      isLive: true,
    },
  ];



  const filteredDemos = activeTab === 'all' ? demos : demos.filter((d) => d.tag === activeTab);

  const tagTone = (tag) => {
    switch (tag) {
      case 'Live Demo':
        return 'border-accent/30 bg-accent/10 text-accent';
      case 'Live Demo Beta':
        return 'border-purple-500/30 bg-purple-500/10 text-purple-200';
      case 'Video Demo Post':
        return 'border-yellow-500/30 bg-yellow-500/10 text-yellow-200';
      case 'Service':
        return 'border-blue-500/30 bg-blue-500/10 text-blue-300';
      case 'Architecture':
        return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300';
      default:
        return 'border-accent/30 bg-accent/10 text-accent';
    }
  };

  const handleViewDemo = (link) => {
    window.open(link, '_blank', 'noopener,noreferrer');
  };


  const DemoCard = ({ demo, index }) => {
    const TagIcon = demo.icon || FaRobot;

    return (
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: inView ? 1 : 0, y: inView ? 0 : 18 }}
        transition={{ duration: 0.45, delay: index * 0.06 }}
        whileHover={{ y: -4 }}
        className="glass-card group overflow-hidden relative"
      >
        {/* Image header */}
        <div className="relative h-32 sm:h-36 md:h-44 overflow-hidden">
          <img
            src={demo.image}
            alt={demo.title}
            loading="lazy"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-primary-950/40 to-transparent" />

          <div className="absolute top-4 left-4 flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center backdrop-blur">
              <TagIcon className="text-accent text-lg" />
            </div>
            <span className="text-[10px] font-display tracking-[0.3em] text-accent/90 border border-accent/20 bg-accent/5 px-2 py-1 rounded-full backdrop-blur">
              DEMO
            </span>
          </div>

          <span
            className="absolute top-4 right-4 inline-flex items-center px-2.5 py-1 rounded-full border text-[10px] font-display tracking-wider border-accent/30 bg-accent/10 text-accent backdrop-blur"
          >
            Live
          </span>
          {demo.tag ? (
            <span
              className={`absolute top-4 right-4 hidden sm:inline-flex items-center px-2.5 py-1 rounded-full border text-[10px] font-display tracking-wider ${tagTone(
                demo.tag
              )} backdrop-blur`}
            >
              {demo.tag}
            </span>
          ) : null}

        </div>

        <div className="p-6 md:p-7 pt-4">
          <h3 className="text-lg md:text-xl font-display font-bold text-white group-hover:text-accent transition-colors mb-2">
            {demo.title}
          </h3>

          <p className="text-gray-300 text-sm leading-relaxed mb-4">{demo.description}</p>

          <p className="text-white font-display font-bold text-sm mb-5">
            <span className="inline-block px-3 py-1 rounded-lg bg-primary-900/40 border border-gray-800">
              Key Outcome: {demo.outcome}
            </span>
          </p>


          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={() => handleViewDemo(demo.link)}
              className="btn-primary group !px-4 !py-2 !text-xs"
            >
              <span>Open Live Demo</span>
              <FaExternalLinkAlt className="text-xs group-hover:translate-x-1 transition-transform" />
            </button>

          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-accent to-transparent transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-primary-950">
      <section className="relative py-16 md:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-5" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-accent/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-accent/3 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="text-accent font-display text-xs tracking-[0.3em]">CRM AUTOMATION</span>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-display font-bold text-white mt-3 mb-3">
              Automation <span className="text-accent">Use Cases</span>
            </h1>
            <p className="text-gray-400 text-sm md:text-base max-w-2xl mx-auto">
              POC-level Salesforce Flow demos focused on execution consistency and measurable outcomes.
            </p>
            <div className="golden-line mx-auto mt-6" />
          </motion.div>
        </div>
      </section>

      <section className="relative py-16 md:py-20" ref={sectionRef}>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-10 md:mb-14"
          >
            <h2 className="text-3xl md:text-5xl font-display font-bold text-white mt-3 mb-4">
              Automation Use Cases <span className="text-accent">Live & Video Demo Posts</span>
            </h2>
            <p className="text-gray-400 text-sm md:text-base max-w-3xl mx-auto">
              Explore real-world CRM automation scenarios implemented using Salesforce Flow. Each demo represents a process-level improvement designed for execution consistency.
            </p>

            <div className="flex flex-wrap justify-center gap-2 mt-7">
              {tabs.map((t) => {
                const isActive = activeTab === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setActiveTab(t.id)}
                    className={`flex items-center px-4 py-2 text-xs font-display tracking-wider transition-all duration-300 rounded-lg ${
                      isActive
                        ? 'bg-accent text-primary-950 shadow-lg shadow-accent/20'
                        : 'bg-primary-800/30 border border-gray-700/50 text-gray-400 hover:border-accent/50 hover:text-accent'
                    }`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-7">
            {filteredDemos.map((demo, index) => (
              <DemoCard key={demo.id} demo={demo} index={index} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;

